import type { DelegationOutcome } from "./delegation.ts";
import type { DecisionKind, RuleEffect, Tier } from "./types.ts";

/** Audit wording rendered from the person's current name. It is deliberately not persisted. */
export function automatedAnswerAudit(personName: string): string {
  return `answered by automation on behalf of ${personName}`;
}

/** Stored on a token an admin minted for someone's automation. The label is only attribution. */
export const AUTOMATION_SCOPE = "automation";

export function isAutomationScope(scope: string | null | undefined): boolean {
  return scope?.trim().toLowerCase() === AUTOMATION_SCOPE;
}

export type AutomationTaskGate = {
  tier: Tier;
  firedRuleEffects: readonly RuleEffect[];
  /** The represented person's current delegation tier ceiling. Null means delegation is off. */
  delegationTiers: readonly Tier[] | null;
};

/**
 * Separation-of-duties checks belong to the automation token's represented person, not to the
 * person's configured review delegate. The delegate still defines policy coverage; this check
 * prevents the separate automation credential from approving its own person's work.
 */
export function automationReviewIdentityRefusal(input: {
  automationActorId: string;
  builderId: string | null;
  builderOwnerId: string | null;
  tier: Tier;
}): string | null {
  if (input.builderId === input.automationActorId) return "automation may not review a change its represented person built";
  if (input.tier === "high" && input.builderOwnerId === input.automationActorId) {
    return "automation may not review a high-risk change built by an agent owned by its represented person";
  }
  return null;
}

const HARD_RULE_EFFECTS: Record<RuleEffect, boolean> = {
  forbid_agent_builder: true,
  require_human_reviewer: true,
  require_focus: true,
  require_reviewer: true,
  min_tier: false,
};

/** The critical-tier and hard-rule stop shared by automated questions and delegated reviews. */
export function automationHardBlock(input: AutomationTaskGate): string | null {
  if (input.tier === "critical") return "critical risk always needs two human reviews";
  if (!input.delegationTiers) return "no delegation policy is set for this person";
  if (!input.delegationTiers.includes(input.tier)) return "this risk tier is outside the person's delegation policy";
  if (input.firedRuleEffects.some((effect) => HARD_RULE_EFFECTS[effect])) return "a fired rule requires a human reviewer";
  return null;
}

const AUTOMATION_REVIEW_KINDS = new Set<DecisionKind>(["review_change", "spot_check"]);

/** Why an automation-scoped token may not answer, or null when the policy permits it. */
export function automatedAnswerRefusal(
  kind: DecisionKind,
  delegation: Pick<DelegationOutcome, "mayDecideAlone" | "why"> | null,
  gate: AutomationTaskGate | null = null,
): string | null {
  if (kind === "question") {
    if (!gate) return "automation may not answer a question that is not tied to a task or an intent";
    return automationHardBlock(gate);
  }
  if (AUTOMATION_REVIEW_KINDS.has(kind)) {
    if (delegation?.mayDecideAlone) return null;
    return delegation?.why ?? "no delegation policy is set for this person";
  }
  return `automation may not answer ${kind} decisions`;
}

const AUTOMATION_READ_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);
const AUTOMATION_READ_ROUTE = /^(\/api)?\/(decisions|tasks|intents)(\/[^/]+)?$/;
const AUTOMATION_ANSWER_ROUTE = /^(\/api)?\/decisions\/[^/]+\/answer$/;

/** Deny-by-default HTTP allowlist for automation-scoped tokens. */
export function automationRouteAllowed(method: string, path: string): boolean {
  const verb = method.toUpperCase();
  const normalized = path.split("?")[0]!.replace(/\/+$/, "") || "/";
  if (AUTOMATION_READ_METHODS.has(verb)) return AUTOMATION_READ_ROUTE.test(normalized);
  return verb === "POST" && AUTOMATION_ANSWER_ROUTE.test(normalized);
}

/** Stored on a personal token a person minted for the Roomate mod (chief-of-office.md §12.3 C21). Not automation: it is that person's own click. */
export const MOD_SCOPE = "mod";

export function isModScope(scope: string | null | undefined): boolean {
  return scope?.trim().toLowerCase() === MOD_SCOPE;
}

const MOD_ROUTES: readonly { method: string; path: RegExp }[] = [
  { method: "GET", path: /^\/chief\/glance$/ },
  { method: "POST", path: /^\/decisions\/[^/]+\/answer$/ },
  { method: "GET", path: /^\/messages$/ },
  { method: "POST", path: /^\/messages$/ },
  { method: "POST", path: /^\/routines\/[^/]+\/pause$/ },
  { method: "POST", path: /^\/chief\/actions\/[^/]+\/undo$/ },
];

/** Deny-by-default HTTP allowlist for `mod`-scoped tokens. Per-route limits (question kind, Chief thread) are enforced in the service. */
export function modRouteAllowed(method: string, path: string): boolean {
  const verb = method.toUpperCase();
  const normalized = (path.split("?")[0]!.replace(/\/+$/, "") || "/").replace(/^\/api(?=\/|$)/, "") || "/";
  return MOD_ROUTES.some((r) => r.method === verb && r.path.test(normalized));
}

/** MCP mirrors the documented decision/task/intent reads and adds the answer tool. */
const AUTOMATION_MCP_TOOLS = new Set(["whoami", "list_tasks", "get_task", "list_decisions", "get_decision", "get_intent", "answer_decision"]);

export function automationMcpToolAllowed(name: string): boolean {
  return AUTOMATION_MCP_TOOLS.has(name);
}
